import React, { useState, useEffect, useMemo, useRef } from "react";

import { formatNumbers, isNonEmptyArray, formatWithTwoDecimals } from "_helpers";

import { Card, Checkbox, Descriptions, Row, Table, Typography } from "antd";

import { columns } from "./_tableConfigs";

import styles from "_styles/FolderDetail.module.css";

export function Invoices({ fetchData }){
    const [ tableDataSource, setTableDataSource ] = useState([]);

    const [ closedInvoicesIsEnable, setClosedInvoicesIsEnable ] = useState(false);
    const [ expandedRowKeys, setExpandedRowKeys ] = useState([]);
    
    const tableRef = useRef();

    const { Text } = Typography;

    const labelStyle = 200;
    const currencySign = fetchData?.inv_currency_sign && fetchData.inv_currency_sign.length === 1 ? fetchData.inv_currency_sign[0] : null;

    const descriptionsItems = [
        { key: '1', label: 'Nb factures', children: fetchData?.inv_nb_open ?? 0, span: 2 },
        { 
            key: '2', 
            label: 'Encours total', 
            children: <span>
                { fetchData?.inv_amount_due ? formatNumbers(fetchData.inv_amount_due) : 0 } €
                {
                    fetchData?.inv_currency_amount_due
                        ?
                            (
                                <span className="ms-1 ">
                                    <Text strong>({ formatNumbers(fetchData.inv_currency_amount_due) } { currencySign })</Text>
                                </span>
                            )
                        :
                            null
                }
            </span> 
        },
        { 
            key: '3', 
            label: 'Encours échu', 
            children: <span>
                { fetchData?.inv_amount_due_over ? formatNumbers(fetchData.inv_amount_due_over) : 0 } €
                {
                    fetchData?.inv_currency_amount_due_over
                        ?
                            (
                                <span className="ms-1 ">
                                    <Text strong>({ formatNumbers(fetchData.inv_currency_amount_due_over) } { currencySign })</Text>
                                </span>
                            )
                        :
                            null
                }
            </span> 
        }
    ].map(el => ({...el, labelStyle: { width: labelStyle }}));

    const computedColumns = useMemo(() => {
        const cols = [...columns];

        if( fetchData?.inv_currency_amount_due ) {
            const index = cols.findIndex(col => col.dataIndex === 'amount');
        
            if( index !== -1 ) {
                cols.splice(index + 1, 0, {
                    title: 'En devise',
                    dataIndex: 'currency_amount',
                    width: 200,
                    align: 'right',
                    className: 'thead-center',
                    render: (value, { _isLeaf, currency_sign }) => {
                        const val = value ? formatWithTwoDecimals(value) : null;
                        return (
                            <>
                                {
                                    val
                                    ?
                                        !_isLeaf
                                        ?
                                            <span style={{fontWeight: 'bold'}}>
                                                {formatNumbers(val)} { currencySign }
                                            </span>
                                        :
                                            <span>
                                                {formatNumbers(val)} { currency_sign ?? null }
                                            </span>
                                    :
                                        null
                                }
                            </>
                        );
                    }
                });
            }
        }

        return cols;
    }, [columns, fetchData])

    useEffect(() => {
        let tempData2 = [], rowKeys = [];

        Object.entries(fetchData).forEach(([key, value]) => {
            if( key === 'records' ){
                value.forEach(val => {
                    const { 
                        notepad_txt_litig,

                        status_color, 
                        status_txt, 
                        status_substatus_txt,
                        status_closed_void,
                        status_closed_end,

                        records,
                        _acc_id,
                        _file_filerecord_id,
                        _status_id
                    } = val;

                    if( !closedInvoicesIsEnable && ( status_closed_void || status_closed_end ) ) return ;

                    rowKeys.push(_file_filerecord_id);

                    let recordRef = status_txt;
                    if( status_substatus_txt ) recordRef+= ' - ' + status_substatus_txt;

                    let tempObjFile = {
                        key: _file_filerecord_id,
                        record_ref: recordRef,
                        date_record: null,
                        amount: null,
                        letter_code: null,
                        _notepad_txt_litig: notepad_txt_litig,
                        _status_color: status_color,
                        _status_closed_void: status_closed_void,
                        _status_closed_end: status_closed_end,
                        _acc_id,
                        _file_filerecord_id,
                        _status_id
                    };

                    if( isNonEmptyArray(records) ){
                        let tempArrRecord = [], sumAmount = 0, sumCurrencyAmount = 0;

                        records.forEach(rec => {
                            const { 
                                record_id,

                                record_ref,
                                date_record,
                                amount,
                                currency_amount,
                                currency_sign,
                                letter_code
                            } = rec;

                            sumAmount+= amount;
                            sumCurrencyAmount+= currency_amount;

                            tempArrRecord.push({
                                key: record_id,
                                record_ref,
                                date_record,
                                amount,
                                currency_amount,
                                currency_sign,
                                letter_code: letter_code || null,
                                _isLeaf: true
                            });
                        })
                        tempObjFile['amount'] = sumAmount;
                        tempObjFile['currency_amount'] = sumCurrencyAmount;
                        tempObjFile['children'] = tempArrRecord;
                    }

                    tempData2.push(tempObjFile);
                })
            }
        })

        setTableDataSource(tempData2);
        setExpandedRowKeys(rowKeys);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchData, closedInvoicesIsEnable])

    return (
        <>
            {
                isNonEmptyArray(descriptionsItems)
                &&
                    <div className="h-100">
                        <Card className="h-100" bodyStyle={{height: '100%'}}>
                            <Descriptions 
                                title={("Pièces").toUpperCase()} 
                                column={1} 
                                bordered 
                                size="small" 
                                items={descriptionsItems}
                            />
                            <Row className="my-1">
                                <Checkbox
                                    checked={closedInvoicesIsEnable}
                                    className="ms-auto"
                                    onChange={() => setClosedInvoicesIsEnable(oldVal => !oldVal)}
                                >
                                    { closedInvoicesIsEnable ? 'Masquer' : 'Afficher' } les pièces lettrées
                                </Checkbox>
                            </Row>
                            <div style={{ height: '40em' }}>
                                <Table 
                                    ref={tableRef}
                                    columns={computedColumns} 
                                    dataSource={tableDataSource} 
                                    className={["mt-2", styles.tableHeight].join(" ")}  
                                    pagination={false}
                                    expandable={{
                                        expandedRowKeys,
                                        onExpandedRowsChange: expandedRows => { setExpandedRowKeys(expandedRows) }
                                    }}
                                    scroll={{
                                        x: '0px',
                                        y: '0px'
                                    }}
                                />
                            </div>
                        </Card>
                    </div>
            }
        </>
    );
}